import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import supabase from './supabase/supabase';
import LandingPage from './components/LandingPage';
import PaymentGate from './components/PaymentGate';
import RegisterForm from './components/RegisterForm';
import LoginForm from './components/LoginForm';
import Dashboard from './components/Dashboard';
import AdminLogin from './components/AdminLogin';
import AdminPanel from './components/AdminPanel';
import ResetPasswordPage from './components/ResetPasswordPage';
import LoadingSpinner from './components/LoadingSpinner';
import Toast from './components/Toast';
import './App.css';

const paypalOptions = {
  'client-id': 'AWfXLNs08045qpiiLudwy9wKuc69gJEg2vu6gUkSCs7JWOzo92AMBAYIGCWzYWKEN46WcRPrt9h2b5RY',
  'vault': true,
  'intent': 'subscription'
};

function App() {
  const [view, setView] = useState('landing');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    // Check for password recovery in URL first
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    
    if (type === 'recovery') {
      setView('reset-password');
      setLoading(false);
      return;
    }

    checkUser();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setView('landing');
        localStorage.removeItem('appView');
      } else if (event === 'PASSWORD_RECOVERY') {
        setView('reset-password');
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (view !== 'landing' && view !== 'loading' && view !== 'reset-password') {
      localStorage.setItem('appView', view);
    }
  }, [view]);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        const { data: userData, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (!error && userData) {
          setUser(userData);
          const savedView = localStorage.getItem('appView');
          if (savedView && ['dashboard', 'admin-panel'].includes(savedView)) {
            setView(savedView);
          } else if (userData.is_admin) {
            setView('admin-panel');
          } else {
            setView('dashboard');
          }
        }
      }
    } catch (error) {
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    if (userData.is_admin) {
      setView('admin-panel');
      localStorage.setItem('appView', 'admin-panel');
    } else {
      setView('dashboard');
      localStorage.setItem('appView', 'dashboard');
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    console.log('Payment success data:', paymentData);
    setSubscriptionData(paymentData);
    setView('register');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setView('landing');
    localStorage.removeItem('appView');
    showToast('Logged out successfully');
  };

  const handleAccessTool = () => {
    setView('dashboard');
    localStorage.setItem('appView', 'dashboard');
  };

  const handleBackToAdmin = () => {
    setView('admin-panel');
    localStorage.setItem('appView', 'admin-panel');
  };

  const handleResetPasswordSuccess = () => {
    showToast('Password reset successfully!');
    // Clear the URL hash
    window.location.hash = '';
    handleLogout();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <PayPalScriptProvider options={paypalOptions}>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <AnimatePresence mode="wait">
          {view === 'landing' && (
            <LandingPage
              key="landing"
              onGetStarted={() => setView('payment')}
              onLogin={() => setView('login')}
            />
          )}
          
          {view === 'payment' && (
            <PaymentGate
              key="payment"
              onPaymentSuccess={handlePaymentSuccess}
              onBack={() => setView('landing')}
              showToast={showToast}
            />
          )}
          
          {view === 'register' && (
            <RegisterForm
              key="register"
              onSuccess={handleLoginSuccess}
              onBack={() => setView('payment')}
              onShowLogin={() => setView('login')}
              showToast={showToast}
              subscriptionData={subscriptionData}
            />
          )}
          
          {view === 'login' && (
            <LoginForm
              key="login"
              onSuccess={handleLoginSuccess}
              onBack={() => setView('landing')}
              onShowRegister={() => setView('payment')}
              showToast={showToast}
            />
          )}

          {view === 'reset-password' && (
            <ResetPasswordPage
              key="reset-password"
              onSuccess={handleResetPasswordSuccess}
              showToast={showToast}
            />
          )}
          
          {view === 'dashboard' && user && (
            <Dashboard
              key="dashboard"
              user={user}
              onLogout={handleLogout}
              onBackToAdmin={user.is_admin ? handleBackToAdmin : null}
              showToast={showToast}
            />
          )}

          {view === 'admin-login' && (
            <AdminLogin
              key="admin-login"
              onSuccess={handleLoginSuccess}
              onBack={() => setView('landing')}
              showToast={showToast}
            />
          )}

          {view === 'admin-panel' && user?.is_admin && (
            <AdminPanel
              key="admin-panel"
              user={user}
              onLogout={handleLogout}
              onAccessTool={handleAccessTool}
              showToast={showToast}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {toast && <Toast message={toast.message} type={toast.type} />}
        </AnimatePresence>
      </div>
    </PayPalScriptProvider>
  );
}

export default App;